import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import type { AlertColor } from "@mui/material/Alert/Alert";
import dayjs from "dayjs";
import { useEffect, useReducer, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { USE_AUTH } from "./api/apiConfig";
import useQueryClient from "./api/query";
import ClaimsDashboard from "./components/ClaimsDashboard";
import Copilot from "./components/Copilot";
import { CredentialsDashboard } from "./components/CredentialsDashboard";
import DocumentsDashboard from "./components/DocumentsDashboard";
import InsuranceArDashboard from "./components/InsuranceArDashboard";
import { InsuranceVerificationDashboard } from "./components/InsuranceVerification";
import {
  initialInsuranceVerificationStore,
  insuranceVerificationStoreReducer,
} from "./components/InsuranceVerification/store";
import LeftNav from "./components/LeftNav";
import Loading from "./components/Loading";
import PageNotFound from "./components/PageNotFound";
import PatientsDashboard, {
  initialPatientStore,
  patientsStoreReducer,
} from "./components/PatientsDashboard";
import PaymentPlans, {
  initialPaymentPlanStore,
  paymentPlanStoreReducer,
} from "./components/PaymentPlans";
import PaymentsDashboard from "./components/PaymentsDashboard";
import type { PaymentDashboardSettings } from "./components/PaymentsDashboard/PaymentsListView";
import ReconciliationDashboard from "./components/ReconciliationDashboard";
import ReportsDashboard from "./components/ReportsDashboard";
import StudioDashboard from "./components/StudioDashboard";
import useFeatureAnalytics from "./hooks/useFeatureAnalytics";
import { trackAppLoaded, trackPracticeFilterChanged } from "./utils/analytics";
import { useConfig } from "./utils/config";
import useDevMode from "./utils/devmode";
import { FEATURE_FLAG_KEYS, useFeatureFlag } from "./utils/featureFlags";
import {
  applyPostHogConfig,
  identifyUser,
  registerPracticeContext,
} from "./utils/posthog";
import {
  clearAppLocalStorage,
  persistLocalStorage,
  retrieveLocalStorage,
} from "./utils/utils";

const { search } = window.location;
const GLOBAL_SELECTED_PRACTICES = "globalSelectedPractices";

const bootstrapCache = new Map<string, unknown>();
const bootstrapInFlight = new Map<string, Promise<unknown>>();

function loadOnce<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  if (bootstrapCache.has(key)) {
    return Promise.resolve(bootstrapCache.get(key) as T);
  }
  if (bootstrapInFlight.has(key)) {
    return bootstrapInFlight.get(key) as Promise<T>;
  }
  const promise = fetcher()
    .then((result) => {
      bootstrapCache.set(key, result);
      return result;
    })
    .finally(() => {
      bootstrapInFlight.delete(key);
    });
  bootstrapInFlight.set(key, promise);
  return promise;
}

interface DashboardSettings {
  payments: PaymentDashboardSettings;
}

function getDashboardSettings(): DashboardSettings {
  const dashboardSettings: DashboardSettings = {
    payments: {
      selectedDate: {
        startDate: dayjs().subtract(1, "month"),
        endDate: dayjs(),
      },
    },
  };
  const dashboardLocalStorage = localStorage.getItem("dashboardSettings");
  if (dashboardLocalStorage) {
    const persistedDashboardSettings: DashboardSettings = JSON.parse(
      dashboardLocalStorage,
    );
    if (persistedDashboardSettings.payments?.selectedDate) {
      dashboardSettings.payments.selectedDate.startDate = dayjs(
        persistedDashboardSettings.payments.selectedDate.startDate,
      );
      dashboardSettings.payments.selectedDate.endDate = dayjs(
        persistedDashboardSettings.payments.selectedDate.endDate,
      );
    }
  }
  return dashboardSettings;
}

function FCPortal({
  updateSnackBar,
}: {
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}) {
  const [organization, setOrganization] = useState<OrganizationMessage | null>(
    null,
  );
  const [practices, setPractices] = useState<PracticeMessage[]>([]);
  const [supportedSources, setSupportedSources] = useState<SourceMessage[]>([]);
  const [supportedPMSes, setSupportedPMSes] = useState<PMSMessage[]>([]);
  const [supportedPayers, setSupportedPayers] = useState<
    CredentialsSupportedPayersMessage[]
  >([]);
  const [selectedPractices, setSelectedPractices] = useState<PracticeMessage[]>(
    [],
  );
  const [dashboardSettings, setDashboardSettings] = useState<DashboardSettings>(
    getDashboardSettings(),
  );

  // We define dashboard specific state at the app level so we don't lose state when we
  // switch tabs.
  const [patientsStore, updatePatientsStore] = useReducer(
    patientsStoreReducer,
    initialPatientStore,
  );

  const [paymentPlanStore, updatePaymentPlanStore] = useReducer(
    paymentPlanStoreReducer,
    initialPaymentPlanStore,
  );

  const [insuranceVerificationStore, updateInsuranceVerificationStore] =
    useReducer(
      insuranceVerificationStoreReducer,
      initialInsuranceVerificationStore,
    );

  const queryClient = useQueryClient();

  const { error: authError, logout, user } = useAuth0();

  useFeatureAnalytics();

  const { isEnabled: insuranceVerificationEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.INSURANCE_VERIFICATION,
  );

  const { isEnabled: insuranceArEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.INSURANCE_AR,
  );

  const devModeEnabled = useDevMode() || !USE_AUTH;

  const { config: appConfig, isLoading: appConfigLoading } = useConfig();

  const setGlobalPractices = (newSelectedPractices: PracticeMessage[]) => {
    trackPracticeFilterChanged(
      selectedPractices.length,
      newSelectedPractices.length,
    );
    persistLocalStorage(GLOBAL_SELECTED_PRACTICES, newSelectedPractices);
    setSelectedPractices(newSelectedPractices);
  };

  const applyPractices = (practiceMessages: PracticeMessage[]) => {
    const sortedPractices = [...practiceMessages].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    setPractices(sortedPractices);
    const localStoragePractices = retrieveLocalStorage<PracticeMessage[]>(
      GLOBAL_SELECTED_PRACTICES,
    );
    const loadedSelectedPractices = localStoragePractices
      ? sortedPractices.filter((practice) =>
          localStoragePractices.some(
            (localPractice) => localPractice.wieldyId === practice.wieldyId,
          ),
        )
      : sortedPractices;
    setSelectedPractices(loadedSelectedPractices);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding queryClient.getOrganization, queryClient.getPractices, queryClient.getSupportedPracticeManagementSystems and 4 more would change when this runs
  useEffect(() => {
    let cancelled = false;

    const fetchOrLogError = <T,>(key: string, fetcher: () => Promise<T>) =>
      loadOnce(key, fetcher).catch((error) => {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error(`Failed to fetch ${key}: ${error}`);
        return null;
      });

    (async () => {
      const [
        organizationResponse,
        practiceMessages,
        sources,
        practiceManagementSystems,
        payers,
      ] = await Promise.all([
        fetchOrLogError("organization", () => queryClient.getOrganization()),
        fetchOrLogError("practices", () => queryClient.getPractices()),
        fetchOrLogError("sources", () => queryClient.getSupportedSources()),
        fetchOrLogError("practice management systems", () =>
          queryClient.getSupportedPracticeManagementSystems(),
        ),
        fetchOrLogError("payers", () =>
          queryClient.getCredentialsSupportedPayers(),
        ),
      ]);

      if (cancelled) {
        return;
      }

      if (organizationResponse) {
        setOrganization(organizationResponse);
      }

      if (practiceMessages) {
        applyPractices(practiceMessages);
      }

      if (sources) {
        setSupportedSources(sources);
      }

      if (practiceManagementSystems) {
        setSupportedPMSes(practiceManagementSystems);
      }

      if (payers) {
        setSupportedPayers(payers);
      }

      trackAppLoaded(practiceMessages?.length ?? 0);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "dashboardSettings",
      JSON.stringify(dashboardSettings),
    );
  }, [dashboardSettings]);

  useEffect(() => {
    if (organization) {
      identifyUser(user, organization.name, practices.length);
    }
  }, [organization, user, practices]);

  useEffect(() => {
    registerPracticeContext(
      user?.org_id,
      selectedPractices.map((practice) => practice.wieldyId),
      selectedPractices.map((practice) => practice.name),
      devModeEnabled,
    );
  }, [selectedPractices, user, devModeEnabled]);

  useEffect(() => {
    if (!appConfigLoading) {
      applyPostHogConfig(user?.org_id, devModeEnabled, appConfig.posthog);
    }
  }, [appConfig, appConfigLoading, user, devModeEnabled]);

  const savePaymentDashboard = (newSettings: PaymentDashboardSettings) => {
    setDashboardSettings({
      ...dashboardSettings,
      payments: newSettings,
    });
  };

  const getDefaultRoute = () => `/copilot${search}`;

  if (authError) {
    clearAppLocalStorage();
    logout();
    return null;
  }
  return (
    <div style={{ display: "flex" }}>
      <LeftNav
        practices={practices}
        selectedPractices={selectedPractices}
        selectPractices={setGlobalPractices}
      >
        {practices.length ? (
          <div className="view">
            <Routes>
              <Route path="/" element={<Navigate to={getDefaultRoute()} />} />
              <Route
                path="/payments/*"
                element={
                  <PaymentsDashboard
                    selectedPractices={selectedPractices}
                    allPractices={practices}
                    dashboardSettings={dashboardSettings.payments}
                    saveDashboardSettings={savePaymentDashboard}
                    updateSnackBar={updateSnackBar}
                    supportedPayers={supportedPayers}
                    supportedSources={supportedSources}
                  />
                }
              />
              <Route
                path="/patients/:patientId/payment-plans/*"
                element={
                  <PaymentPlans
                    patientStore={patientsStore}
                    updatePatientStore={updatePatientsStore}
                    store={paymentPlanStore}
                    updateStore={updatePaymentPlanStore}
                    queryClient={queryClient}
                    updateSnackBar={updateSnackBar}
                  />
                }
              />
              <Route
                path="/patients/*"
                element={
                  <PatientsDashboard
                    updateSnackBar={updateSnackBar}
                    practices={practices}
                    selectedPractices={selectedPractices}
                    supportedSources={supportedSources}
                  />
                }
              />
              <Route path="/documents" element={<DocumentsDashboard />} />
              <Route
                path="/claims/*"
                element={
                  <ClaimsDashboard
                    updateSnackBar={updateSnackBar}
                    selectedPractices={selectedPractices}
                    supportedPayers={supportedPayers}
                    supportedSources={supportedSources}
                  />
                }
              />
              <Route
                path="/reconciliation/*"
                element={
                  <ReconciliationDashboard
                    selectedPractices={selectedPractices}
                    updateSnackBar={updateSnackBar}
                  />
                }
              />
              {insuranceArEnabled && (
                <Route
                  path="/insurance-ar/*"
                  element={
                    <InsuranceArDashboard
                      queryClient={queryClient}
                      selectedPractices={selectedPractices}
                      updateSnackBar={updateSnackBar}
                      practices={practices}
                      supportedSources={supportedSources}
                    />
                  }
                />
              )}
              <Route
                path="/reports/*"
                element={
                  <ReportsDashboard
                    queryClient={queryClient}
                    selectedPractices={selectedPractices}
                    organization={organization}
                  />
                }
              />
              <Route
                path="/credentials"
                element={
                  <CredentialsDashboard
                    practices={practices}
                    updateSnackBar={updateSnackBar}
                    supportedSources={supportedSources}
                    supportedPMSes={supportedPMSes}
                    selectedPractices={selectedPractices}
                  />
                }
              />
              <Route
                path="/Copilot"
                element={
                  <Copilot
                    fullname={user?.name ? user.name : ""}
                    selectedPractices={selectedPractices}
                  />
                }
              />
              {insuranceVerificationEnabled && (
                <Route
                  path="/insurance-verification/*"
                  element={
                    <InsuranceVerificationDashboard
                      store={insuranceVerificationStore}
                      updateStore={updateInsuranceVerificationStore}
                      queryClient={queryClient}
                      updateSnackBar={updateSnackBar}
                      selectedPractices={selectedPractices}
                      supportedPayers={supportedPayers}
                    />
                  }
                />
              )}
              {devModeEnabled && (
                <Route
                  path="/studio/*"
                  element={
                    <StudioDashboard
                      selectedPractices={selectedPractices}
                      supportedSources={supportedSources}
                      updateSnackBar={updateSnackBar}
                    />
                  }
                />
              )}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </div>
        ) : (
          <Loading />
        )}
      </LeftNav>
    </div>
  );
}

export default (() => {
  if (USE_AUTH) {
    return withAuthenticationRequired(FCPortal);
  }
  return FCPortal;
})();
